import { useState, useRef, useEffect } from "react";
import { useDragDropManager } from "react-dnd";

/**
 * Custom hook that provides auto-scrolling functionality during drag operations.
 * Automatically scrolls the nearest scrollable container when dragging near its edges.
 * Basically we look for the nearest scrollable ancestor and scroll it vertically.
 *
 * @returns {boolean} isDragging - Whether a drag operation is currently active
 */
export const useDragAutoScroll = () => {
  const dragDropManager = useDragDropManager();
  const monitor = dragDropManager.getMonitor();
  const [isDragging, setIsDragging] = useState(false);
  const timerRef = useRef(null);
  const unsubscribeOffsetRef = useRef(null);
  const unsubscribeStateRef = useRef(null);

  // Helper: find nearest scrollable ancestor
  const getScrollableParent = (el) => {
    if (!el) return null;
    let node = el;
    while (node) {
      try {
        //get the style of the parent and check if it has overflowY set to auto or scroll, if it has we return this node
        const style = window.getComputedStyle(node);
        const overflowY = style ? style.overflowY : "";
        const canScroll =
          (overflowY === "auto" || overflowY === "scroll") &&
          node.scrollHeight > node.clientHeight;
        if (canScroll) return node;
      } catch {
        // Ignore styling errors
      }
      node = node.parentElement;
    }
    return null;
  };

  // Start an interval to scroll a container vertically at given speed
  const startScrollInterval = (container, speed) => {
    if (!container) return;
    if (timerRef.current) clearInterval(timerRef.current);
    //every 16ms we scroll the container by the given speed
    timerRef.current = setInterval(() => {
      container.scrollBy({ top: speed, behavior: "auto" });
    }, 16); // ~60fps
  };

  // Stop scrolling
  const stopScrollInterval = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Subscribe to drag state changes (start/stop)
  useEffect(() => {
    unsubscribeStateRef.current = monitor.subscribeToStateChange(() => {
      const dragging = monitor.isDragging();
      //that triggers the useEffect below if we are dragging, if not it returns and we stop the interval
      setIsDragging(dragging);
      if (!dragging) {
        stopScrollInterval();
        if (unsubscribeOffsetRef.current) {
          //we unsubscribe from the offset change listener when we stop dragging
          unsubscribeOffsetRef.current();
          unsubscribeOffsetRef.current = null;
        }
      }
    });

    return () => {
      if (unsubscribeStateRef.current) unsubscribeStateRef.current();
      if (unsubscribeOffsetRef.current) unsubscribeOffsetRef.current();
      stopScrollInterval();
    };
  }, [monitor]);

  // When dragging, subscribe to mouse offset changes and auto-scroll
  useEffect(() => {
    if (!isDragging) return;
    unsubscribeOffsetRef.current = monitor.subscribeToOffsetChange(() => {
      const offset = monitor.getClientOffset();
      if (!offset) return;

      const { x, y } = offset;
      //elementFromPoint could be a reliability issue
      //elementFromPoint gets the element at the given coordinates, in our case the element under the mouse cursor
      const el = document.elementFromPoint(x, y);
      //we look for the nearest scrollable parent
      const container = getScrollableParent(el);
      if (!container) {
        stopScrollInterval();
        return;
      }

      //when found a scrollable parent, we check if the mouse is near its edges, rect is the smallest rectangle that contains the element
      const rect = container.getBoundingClientRect();
      const threshold = Math.min(200, rect.height / 3); // dynamic threshold

      if (y < rect.top + threshold) {
        // scroll up
        startScrollInterval(container, -8);
      } else if (y > rect.bottom - threshold) {
        // scroll down
        startScrollInterval(container, 8);
      } else {
        // middle area -> stop
        stopScrollInterval();
      }
    });

    return () => {
      if (unsubscribeOffsetRef.current) unsubscribeOffsetRef.current();
      stopScrollInterval();
    };
  }, [isDragging, monitor]);

  return isDragging;
};
